import type { RIconComponent } from '@ben/orion/remix-icon';
import type Form from '@rjsf/core';
import type { RJSFSchema, RJSFValidationError } from '@rjsf/utils';
import { pick } from 'lodash';
import type { JSX, RefObject } from 'react';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { IconName } from '../../icon/icon';
import type { SchemaFormProps } from '../schema-form';
import SchemaForm from '../schema-form';
import type { SchemaFormData } from '../utils';
import { getFields, mergeSchemas, parseFormData } from '../utils';

export interface SchemaFormPage {
  id?: string;
  title?: string;
  description?: string;
  iconName?: IconName;
  remixIcon?: RIconComponent;
  schema: RJSFSchema;
  render?: (
    formData: SchemaFormData,
    pageInfo: SchemaFormPageInfo[]
  ) => JSX.Element;
}

export interface PagedSchemaFormProps extends Omit<
  SchemaFormProps,
  'onChange' | 'schema'
> {
  pages: SchemaFormPage[];
  currentPage: number;
  onChange?: (pages: SchemaFormPageInfo[]) => void;
  onError?: () => void;
  liveValidate?: boolean;
}

/** Like `SchemaFormPage` but with extra info about each page, like data and errors */
export interface SchemaFormPageInfo extends SchemaFormPage {
  isPageValid?: boolean; // `undefined` when page has not been validated yet
  pageErrors?: RJSFValidationError[];
  pageData: Partial<SchemaFormData>;
}

const PagedSchemaForm = ({
  pages,
  currentPage,
  onChange,
  onError,
  onSubmit,
  formOptions,
  liveValidate,
  ...props
}: PagedSchemaFormProps) => {
  const schema = useMemo(
    () => mergeSchemas(pages.map((page) => page.schema).filter(Boolean)),
    [pages]
  );
  const fields = useMemo(
    () => getFields(pages[currentPage]?.schema),
    [pages, currentPage]
  );
  const render = pages[currentPage].render;
  const [formData, setFormData] = useState<SchemaFormData>(
    props.formData ?? {}
  );
  const [formErrors, setFormErrors] = useState<RJSFValidationError[]>();
  const formRef = useRef<Form>(null);

  useEffect(() => {
    // in case form data is invalidated from BE this will keep the form values in sync
    setFormData((parseFormData(props.formData) ?? {}) as SchemaFormData);
  }, [props.formData]);

  const pageInfo = useMemo(() => {
    return pages.map((page): SchemaFormPageInfo => {
      const pageErrors = formErrors?.filter(
        (error) =>
          error.property &&
          getFields(page.schema).includes(error.property.replace('.', ''))
      );
      return {
        ...page,
        pageErrors,
        isPageValid: pageErrors ? pageErrors.length === 0 : undefined,
        pageData: pick(formData, getFields(page.schema)),
      };
    });
  }, [pages, formErrors, formData]);

  const handleSubmit = useCallback(
    (formData: SchemaFormData) => {
      // Set form errors to empty array to mark all pages as valid
      setFormErrors([]);
      onSubmit(formData);
    },
    [onSubmit]
  );

  const handleOnError = useCallback(
    (errors: RJSFValidationError[]) => {
      setFormErrors(errors);
      onError?.();
    },
    [onError]
  );

  const mergedFormOptions = useMemo(
    () => ({
      showFields: fields,
      ...formOptions,
    }),
    [fields, formOptions]
  );

  useEffect(() => {
    const numberOfErrors = formRef?.current?.state.errors.length;

    // to remove all errors when there is no errors
    if (
      numberOfErrors === 0 &&
      (liveValidate || (formErrors?.length ?? 0) > 0)
    ) {
      setFormErrors((previous) =>
        previous && previous.length === 0 ? previous : []
      );
    }
    // to validate the form on every form data change once there is errors to immediately remove them
    if (numberOfErrors !== 0) {
      formRef?.current?.validateForm();
    }
  }, [liveValidate, formData, formErrors?.length]);

  useEffect(() => {
    // Call onChange with latest page info on every form data
    // change, and every form errors change (on failed submit)
    onChange?.(pageInfo);
  }, [onChange, pageInfo]);

  return (
    <>
      {render?.(formData, pageInfo)}
      <SchemaForm
        {...props}
        onSubmit={handleSubmit}
        onChange={setFormData}
        onError={handleOnError}
        formData={formData}
        formOptions={mergedFormOptions}
        schema={schema}
        liveValidate={liveValidate}
        ref={formRef as RefObject<Form<unknown, RJSFSchema>>}
        focusOnFirstError={false}
      />
    </>
  );
};

export default memo(PagedSchemaForm);
